import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsAuthenticated } from "../authentication/authenticationSlice";
import {
  selectSubscriptionLoadingStatus,
  selectSubscriptionStatus,
} from "./subscription-slice";
import { fetchSubscriptionStatus } from "./slice-extensions/subscription-slice-extension";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ROUTES } from "../../routes";

/**
 * App-wide subscription messaging (the instructor paying us — never member
 * billing): a subtle trial countdown that escalates to a banner in the final
 * two weeks, and a read-only banner with a subscribe CTA after expiry.
 */
const SubscriptionBanner = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const status = useAppSelector(selectSubscriptionStatus);
  const loadingStatus = useAppSelector(selectSubscriptionLoadingStatus);

  useEffect(() => {
    if (isAuthenticated && !status && loadingStatus === LoadingStatus.Idle) {
      dispatch(fetchSubscriptionStatus());
    }
  }, [isAuthenticated, status, loadingStatus, dispatch]);

  // The subscription page carries the full story itself — no banner on top.
  if (!isAuthenticated || !status || location.pathname === ROUTES.SUBSCRIPTION) {
    return null;
  }

  if (status.isReadOnly) {
    return (
      <div className="bg-red-100 dark:bg-red-900/30 px-4 py-3">
        <div className="mx-auto flex max-w-[960px] flex-col items-center gap-2 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <p className="font-semibold text-red-800 dark:text-red-300">
              {t("subscription.banner.readOnlyTitle")}
            </p>
            <p className="text-sm text-red-800 dark:text-red-300">
              {t("subscription.banner.readOnlyBody")}
            </p>
          </div>
          <Link to={ROUTES.SUBSCRIPTION} className="btn btn-primary shrink-0">
            {t("subscription.banner.subscribe")}
          </Link>
        </div>
      </div>
    );
  }

  if (status.isTrialing && status.isTrialUrgent) {
    return (
      <div className="bg-primary-95 dark:bg-primary-20 px-4 py-3">
        <div className="mx-auto flex max-w-[960px] flex-col items-center gap-2 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <p className="font-semibold text-primary-30 dark:text-primary-90">
              {t("subscription.banner.trialEndingTitle", {
                count: status.trialDaysRemaining,
              })}
            </p>
            <p className="text-sm text-primary-30 dark:text-primary-90">
              {t("subscription.banner.trialEndingBody")}
            </p>
          </div>
          <Link to={ROUTES.SUBSCRIPTION} className="btn btn-primary shrink-0">
            {t("subscription.banner.subscribe")}
          </Link>
        </div>
      </div>
    );
  }

  if (status.isTrialing) {
    return (
      <div className="bg-primary-95 dark:bg-primary-20 px-4 py-1.5 text-center text-sm text-primary-30 dark:text-primary-90">
        {t("subscription.banner.trialCountdown", {
          count: status.trialDaysRemaining,
        })}{" "}
        <Link
          to={ROUTES.SUBSCRIPTION}
          className="text-primary-40 dark:text-primary-90 hover:underline"
        >
          {t("subscription.banner.seePlans")}
        </Link>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
